import { Injectable } from "@angular/core";
import { ApiPrefacturationService } from "./api/api-prefacturation.service";
import { StorageService } from "./storage.service";
import { AuthService } from "./auth.service";
import { MissionFacturable } from "../interfaces/mission-facturable";
import { PrefacturationResume } from "../interfaces/prefacturation";

/**
 * Prefixes des cles de persistance du cache facturation (FR-185). La cle finale
 * est suffixee par l'identifiant de l'intervenant : sur un appareil partage, le
 * cache d'un compte ne doit jamais pouvoir s'afficher sous un autre (review
 * Greptile PR #300). Le suffixe rend aussi le nettoyage verifiable — toute cle
 * portant ces prefixes sans correspondre au compte courant est supprimee.
 */
const PREFIXE_MISSIONS_PERSISTE = "facturation_missions_defaut";
const PREFIXE_PREFACS_PERSISTE = "facturation_prefacs_annee_courante";

/**
 * Garde-fou de taille : Preferences (SharedPreferences / UserDefaults) charge
 * la valeur entiere en memoire. Au-dela, on renonce a persister plutot que de
 * penaliser le demarrage de l'app pour un simple accelerateur d'affichage.
 */
const TAILLE_MAX_PERSISTANCE = 1_500_000;

/**
 * Cache mémoire du module facturation (FR-185) : les onglets Aide facturation et
 * Préfacturations naviguent en navigateRoot (chaque bascule détruit la page), ce
 * service conserve les données pour un affichage immédiat au retour.
 *
 * Le cache est un accélérateur d'affichage, PAS une source de vérité
 * (stale-while-revalidate) : les pages pré-affichent `missionsEnCache` /
 * `prefacturationsEnCache` puis rappellent systématiquement `getMissions` /
 * `getPrefacturations`, qui interrogent toujours l'API et remplacent le cache —
 * sans ce rechargement, un utilisateur qui ne pense pas à rafraîchir peut
 * croire à des données manquantes.
 *
 * Deux modes pour les missions, chargés intégralement en une requête : « non
 * préfacturées » (vue par défaut, liste réduite côté serveur) et « Afficher
 * tout » (la pagination FR-185 a été abandonnée : compteur, recherche, filtres
 * et tri portent sur la liste complète).
 *
 * Le cache mémoire est doublé d'une persistance (Preferences) pour les deux vues
 * par défaut — missions non préfacturées et préfacturations de l'année en cours.
 * Sans elle, le premier accès de chaque session repartait d'un écran vide avec
 * spinner. Les vues « Afficher tout » ne sont pas persistées : ce sont les plus
 * volumineuses et les moins consultées.
 */
@Injectable({
  providedIn: "root",
})
export class FacturationDataService {
  /**
   * Filtre année de l'onglet Préfacturations (année en cours par défaut — CDC
   * FR-185, null = tout). Porté ici pour survivre à la bascule entre onglets.
   */
  anneePrefacturations: number | null = new Date().getFullYear();

  /** Clé : mode nonPrefacturees. */
  private missionsCache = new Map<boolean, MissionFacturable[]>();

  /** Clé : année filtrée (ou "toutes"). */
  private prefacturationsCache = new Map<string, PrefacturationResume[]>();

  /**
   * Jetons de fraîcheur par clé de cache (anti-course, review Greptile PR #295) :
   * deux rechargements SWR d'une même clé peuvent se chevaucher, et le plus
   * ancien terminer en dernier. Chaque requête prend le jeton courant + 1 au
   * lancement ; à la réponse, si le jeton n'est plus le dernier (rechargement
   * plus récent, ou invalider() entre-temps), la réponse est périmée : elle
   * n'écrit pas le cache et l'appelant reçoit l'état le plus frais connu.
   */
  private jetonsMissions = new Map<boolean, number>();
  private jetonsPrefacturations = new Map<string, number>();

  /** Hydratation depuis le stockage : déclenchée une seule fois par session. */
  private hydratation: Promise<void> | null = null;

  constructor(
    private api: ApiPrefacturationService,
    private storage: StorageService,
    private auth: AuthService
  ) {}

  /**
   * Identifiant de l'intervenant connecte, suffixe des cles de persistance.
   * Null (pas de session lisible) = on ne persiste ni n'hydrate rien : mieux
   * vaut perdre l'accelerateur d'affichage que risquer une cle non attribuee.
   */
  private async suffixeUtilisateur(): Promise<string | null> {
    try {
      const user: any = await this.auth.getUser();
      const id = user?.["id"];
      return typeof id === "string" && id.length > 0 ? id : null;
    } catch {
      return null;
    }
  }

  /**
   * Recharge en mémoire les listes persistées à la session précédente. À appeler
   * avant de lire `missionsEnCache` / `prefacturationsEnCache` : sans cela, le
   * pré-affichage instantané ne s'applique qu'à partir du 2e accès de la session.
   * Idempotent, et sans effet si le cache mémoire est déjà renseigné.
   */
  hydrater(): Promise<void> {
    this.hydratation ??= this.hydraterDepuisStockage();
    return this.hydratation;
  }

  private async hydraterDepuisStockage(): Promise<void> {
    const suffixe = await this.suffixeUtilisateur();
    await this.purgerCachesEtrangers(suffixe);
    if (!suffixe) {
      return;
    }

    const [missions, prefacs] = await Promise.all([
      this.storage.get<MissionFacturable[]>(
        `${PREFIXE_MISSIONS_PERSISTE}_${suffixe}`
      ),
      this.storage.get<PrefacturationResume[]>(
        `${PREFIXE_PREFACS_PERSISTE}_${suffixe}`
      ),
    ]);

    // Le cache mémoire prime : une requête lancée pendant l'hydratation a pu
    // répondre avant elle, et sa réponse est plus fraîche que le stockage.
    if (missions && !this.missionsCache.has(true)) {
      this.missionsCache.set(true, missions);
    }
    const cleAnneeCourante = String(new Date().getFullYear());
    if (prefacs && !this.prefacturationsCache.has(cleAnneeCourante)) {
      this.prefacturationsCache.set(cleAnneeCourante, prefacs);
    }
  }

  /**
   * Supprime tout cache facturation qui n'appartient pas au compte courant.
   * Filet de sécurité pour les cas où la purge du logout n'a pas eu lieu (appli
   * tuée en cours de déconnexion, plantage) : sans lui, des montants d'un autre
   * intervenant pourraient rester lisibles sur l'appareil.
   */
  private async purgerCachesEtrangers(suffixe: string | null): Promise<void> {
    const prefixes = [PREFIXE_MISSIONS_PERSISTE, PREFIXE_PREFACS_PERSISTE];
    const cles = await this.storage.keys();

    await Promise.all(
      cles
        .filter(
          (cle) =>
            prefixes.some((prefixe) => cle.startsWith(prefixe)) &&
            (!suffixe || !cle.endsWith(`_${suffixe}`))
        )
        .map((cle) => this.storage.remove(cle).catch(() => undefined))
    );
  }

  /**
   * Précharge en tâche de fond la liste de l'onglet voisin, si elle n'est pas
   * déjà en cache : la bascule entre « Aide facturation » et « Préfacturations »
   * se fait alors sans attente. Échec ignoré — l'onglet rechargera de lui-même.
   */
  prechargerPrefacturations(): void {
    if (this.prefacturationsEnCache()) {
      return;
    }
    this.getPrefacturations().catch(() => undefined);
  }

  /** Pendant de `prechargerPrefacturations` pour la vue missions par défaut. */
  prechargerMissions(nonPrefacturees = true): void {
    if (this.missionsEnCache(nonPrefacturees)) {
      return;
    }
    this.getMissions(nonPrefacturees).catch(() => undefined);
  }

  /** Dernière liste chargée du mode demandé (pré-affichage), sinon null. */
  missionsEnCache(nonPrefacturees: boolean): MissionFacturable[] | null {
    return this.missionsCache.get(nonPrefacturees) ?? null;
  }

  /** Dernière liste chargée du filtre année courant (pré-affichage), sinon null. */
  prefacturationsEnCache(): PrefacturationResume[] | null {
    return this.prefacturationsCache.get(this.clePrefacturations()) ?? null;
  }

  /** Missions facturables du mode demandé — toujours l'API, met à jour le cache. */
  async getMissions(nonPrefacturees: boolean): Promise<MissionFacturable[]> {
    const jeton = (this.jetonsMissions.get(nonPrefacturees) ?? 0) + 1;
    this.jetonsMissions.set(nonPrefacturees, jeton);

    const res = await this.api.getMissionsFacturables(
      nonPrefacturees ? { nonPrefacturees: true } : {}
    );

    if (this.jetonsMissions.get(nonPrefacturees) !== jeton) {
      // Réponse périmée : le cache porte (ou portera) l'état de la requête la
      // plus récente — ne pas l'écraser, servir le plus frais connu.
      return this.missionsCache.get(nonPrefacturees) ?? res.items;
    }

    this.missionsCache.set(nonPrefacturees, res.items);
    if (nonPrefacturees) {
      this.persister(PREFIXE_MISSIONS_PERSISTE, res.items);
    }
    return res.items;
  }

  /** Préfacturations du filtre année courant — toujours l'API, met à jour le cache. */
  async getPrefacturations(): Promise<PrefacturationResume[]> {
    const cle = this.clePrefacturations();
    const jeton = (this.jetonsPrefacturations.get(cle) ?? 0) + 1;
    this.jetonsPrefacturations.set(cle, jeton);

    const liste = await this.api.getPrefacturations(
      this.anneePrefacturations ?? undefined
    );

    if (this.jetonsPrefacturations.get(cle) !== jeton) {
      return this.prefacturationsCache.get(cle) ?? liste;
    }

    this.prefacturationsCache.set(cle, liste);
    if (cle === String(new Date().getFullYear())) {
      this.persister(PREFIXE_PREFACS_PERSISTE, liste);
    }
    return liste;
  }

  /**
   * Écriture best-effort du cache persistant : jamais bloquante et jamais fatale
   * (le cache mémoire, lui, est déjà à jour), et abandonnée au-delà du seuil de
   * taille pour ne pas alourdir le démarrage de l'app.
   */
  private persister<T>(prefixe: string, valeur: T): void {
    let charge: string;
    try {
      charge = JSON.stringify(valeur);
    } catch {
      return;
    }

    void this.suffixeUtilisateur().then((suffixe) => {
      if (!suffixe) {
        return;
      }
      const cle = `${prefixe}_${suffixe}`;
      if (charge.length > TAILLE_MAX_PERSISTANCE) {
        return this.storage.remove(cle).catch(() => undefined);
      }
      return this.storage.set(cle, valeur).catch(() => undefined);
    });
  }

  /**
   * Vide tout le cache — après génération d'une préfacturation, les listes des
   * deux onglets et des deux modes sont périmées et ne doivent même plus servir
   * de pré-affichage. Les jetons sont bumpés pour disqualifier les requêtes
   * encore en vol : leur réponse ne doit pas repeupler le cache avec des
   * données d'avant la mutation.
   */
  invalider() {
    this.missionsCache.clear();
    this.prefacturationsCache.clear();
    // Purge du stockage : au logout, appeler AVANT d'effacer la session, le
    // suffixe de clé se déduit du compte courant. Le filet de
    // `purgerCachesEtrangers` couvre le cas où elle n'aurait pas eu lieu.
    void this.suffixeUtilisateur().then((suffixe) => {
      if (!suffixe) {
        return;
      }
      this.storage
        .remove(`${PREFIXE_MISSIONS_PERSISTE}_${suffixe}`)
        .catch(() => undefined);
      this.storage
        .remove(`${PREFIXE_PREFACS_PERSISTE}_${suffixe}`)
        .catch(() => undefined);
    });
    // L'hydratation doit rejouer pour le prochain compte.
    this.hydratation = null;

    for (const [cle, jeton] of this.jetonsMissions) {
      this.jetonsMissions.set(cle, jeton + 1);
    }
    for (const [cle, jeton] of this.jetonsPrefacturations) {
      this.jetonsPrefacturations.set(cle, jeton + 1);
    }
  }

  private clePrefacturations(): string {
    return String(this.anneePrefacturations ?? "toutes");
  }
}
